/**
 * WasteSummaryStep — Step 6 of the waste tracking wizard.
 * Read-only summary of all waste categories, yield rate, and totals.
 * Extracted from MobileWasteTracking.tsx for maintainability.
 */
import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatYield } from '../../../hooks/useYieldFactor';
import { FP_NI_SUMMARY_CARD_CLASSES } from '../../../pages/Inventory/inventoryHelpers';
import type { ByproductMapping } from '../../../services/byproductMappingService';
import type { ByproductEntry } from '../../../utils/mobileSessionStorage';
import type { ScannedBox } from '../wasteTrackingTypes';
import CarryoverIndicator from '../../../components/CarryoverIndicator';

// Buffer state for a single byproduct item (carry-forward info)
export interface MarketableBufferInfo {
  byproductItemNumber: string;
  sourceMaterialItemNumber: string;
  reservedWeight: number;       // Carry-forward reserve from prior sessions
  readyCaseCount: number;
  readyCaseWeight: number;
  totalBufferWeight: number;    // reserved + ready case weights
  lastSessionNumber: string | null;
}

// FP-NI carryover lot allocated to this sub-session (WCO-2116)
export interface FinishProductCarryover {
  lot_id: string;
  item_number: string;
  item_name: string;
  allocated_weight: number | string;
  breakdown: Array<{
    source_sub_session_number: string;
    source_session_number: string | null;
    weight: number;
  }>;
}

interface WasteSummaryStepProps {
  initialMaterial: number;
  totalOutput: number;
  packedBoxCount: number;
  disposable: number;
  humanError: number;
  byproductMarketableEntries: ByproductEntry[];
  byproductProductionEntries: ByproductEntry[];
  byproductMappings: ByproductMapping[];
  marketableBufferInfo?: MarketableBufferInfo[]; // Carry-forward info for each byproduct
  finishProductCarryover?: FinishProductCarryover[]; // FP-NI lots allocated to this sub-session
  remainderNotIntact: number;
  finishProductNotIntact: number;
  remainderIntact: number;
  scannedBoxes: ScannedBox[];
  waterLiquid: number;
  waterLiquidRaw: number;
  waterLiquidNegative: boolean;
  yieldRate: number;
  goBack: () => void;
  goNext: () => void;
}

const WasteSummaryStep: React.FC<WasteSummaryStepProps> = ({
  initialMaterial,
  totalOutput,
  packedBoxCount,
  disposable,
  humanError,
  byproductMarketableEntries,
  byproductProductionEntries,
  byproductMappings,
  marketableBufferInfo = [],
  finishProductCarryover = [],
  remainderNotIntact,
  finishProductNotIntact,
  remainderIntact,
  scannedBoxes,
  waterLiquid,
  waterLiquidRaw,
  waterLiquidNegative,
  yieldRate,
  goBack,
  goNext,
}) => {
  const { t } = useLanguage();
  const [showErrorModal, setShowErrorModal] = useState(false);

  // Show modal automatically when entering this step with invalid totals
  useEffect(() => {
    if (waterLiquidNegative) {
      setShowErrorModal(true);
    }
  }, [waterLiquidNegative]);

  const handleSubmitClick = () => {
    if (waterLiquidNegative) {
      setShowErrorModal(true);
    } else {
      goNext();
    }
  };

  // Excess = magnitude by which recorded totals overshoot raw material. When the
  // balance is invalid, waterLiquidRaw is the signed deficit (negative), so |raw|
  // is the excess. Math.max(0, …) guards against showing "Excess: 0" when valid.
  const excess = Math.max(0, -waterLiquidRaw).toFixed(2);

  return (
    <>
      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t('mobile.cannotSubmit') || 'Cannot Submit'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                {t('mobile.weightsExceedWarning') || 'Entered weights exceed initial material'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('mobile.excessAmount') || 'Excess'}: <span className="font-bold text-red-600 dark:text-red-400">{excess} lbs</span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('mobile.goBackAndCorrect') || 'Go back and correct the values before submitting.'}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold text-sm"
                >
                  {t('common.dismiss') || 'Dismiss'}
                </button>
                <button
                  onClick={() => { setShowErrorModal(false); goBack(); }}
                  className="flex-1 py-3 bg-red-600 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-1.5"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('common.goBack') || 'Go Back'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-y-auto">
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white text-center mb-3">
          {t('mobile.wasteSummary') || 'Waste Summary'}
        </h3>

        {/* Material & Output info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 md:p-4 space-y-1.5 mb-3">
          <div className="flex justify-between text-sm md:text-base">
            <span className="text-gray-500">{t('mobile.initialMaterial') || 'Initial Material'}</span>
            <span className="font-medium text-gray-900 dark:text-white">{initialMaterial.toFixed(2)} lbs</span>
          </div>
          <div className="flex justify-between text-sm md:text-base">
            <span className="text-gray-500">{t('mobile.totalOutput') || 'Total Output'}</span>
            <span className="font-medium text-gray-900 dark:text-white">{totalOutput.toFixed(2)} lbs ({packedBoxCount} boxes)</span>
          </div>
          <CarryoverIndicator allocations={finishProductCarryover} className="flex justify-end -mt-1" tooltipAlign="right" />
        </div>

        {/* Actual Waste group */}
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase">{t('operator.countsAsWaste') || 'Actual Waste'}</span>
            <div className="flex-1 h-px bg-red-200 dark:bg-red-800" />
          </div>
          <div className="space-y-1.5">
            {/* Disposable - Red */}
            <div className="flex justify-between items-center px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <span className="text-sm md:text-base font-medium text-red-900 dark:text-red-100">{t('mobile.disposableWaste') || 'Disposable'}</span>
              <span className="text-sm md:text-base font-bold text-red-700 dark:text-red-300">{disposable.toFixed(2)} lbs</span>
            </div>
            {/* Human Error - Teal */}
            <div className="flex justify-between items-center px-3 py-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
              <span className="text-sm md:text-base font-medium text-teal-900 dark:text-teal-100">
                {t('mobile.humanError') || 'Human Error'}
                <span className="ml-1 text-xs text-teal-500 dark:text-teal-400">({t('mobile.trackingOnly') || 'Tracking only'})</span>
              </span>
              <span className="text-sm md:text-base font-bold text-teal-700 dark:text-teal-300">{humanError.toFixed(2)} lbs</span>
            </div>
            {/* Water/Liquid - Blue (auto-calculated) */}
            <div className={`flex justify-between items-center px-3 py-2 rounded-lg border ${
              waterLiquidNegative
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            }`}>
              <span className={`text-sm md:text-base font-medium ${waterLiquidNegative ? 'text-red-900 dark:text-red-100' : 'text-blue-900 dark:text-blue-100'}`}>
                {t('mobile.waterLiquid') || 'Water / Liquid'}
                <span className="ml-1 text-xs opacity-70">({t('mobile.waterLiquidAuto') || 'auto'})</span>
              </span>
              <span className={`text-sm md:text-base font-bold ${waterLiquidNegative ? 'text-red-700 dark:text-red-300' : 'text-blue-700 dark:text-blue-300'}`}>{waterLiquid.toFixed(2)} lbs</span>
            </div>
          </div>
        </div>

        {/* Returns to Inventory group */}
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase">{t('operator.notWaste') || 'Returns to Inventory'}</span>
            <div className="flex-1 h-px bg-green-200 dark:bg-green-800" />
          </div>
          <div className="space-y-1.5">
            {/* Finish Product Not Intact — canonical FP-NI styling (FP_NI_SUMMARY_CARD_CLASSES) */}
            <div className={`flex justify-between items-center px-3 py-2 ${FP_NI_SUMMARY_CARD_CLASSES.container}`}>
              <span className={`text-sm md:text-base font-medium ${FP_NI_SUMMARY_CARD_CLASSES.label}`}>{t('mobile.finishProductNotIntact') || 'Finish Product - Not Intact'}</span>
              <span className={`text-sm md:text-base font-bold ${FP_NI_SUMMARY_CARD_CLASSES.value}`}>{finishProductNotIntact.toFixed(2)} lbs</span>
            </div>
            {/* By-Product Marketable entries - Yellow */}
            {byproductMarketableEntries.filter(e => e.byproductItemNumber && parseFloat(e.weight) > 0).length > 0 ? (
              byproductMarketableEntries.filter(e => e.byproductItemNumber && parseFloat(e.weight) > 0).map((entry, idx) => {
                const sessionWeight = parseFloat(entry.weight) || 0;
                const mapping = byproductMappings.find(m => m.byproduct_item_number === entry.byproductItemNumber);
                // Find buffer info for this byproduct to show carry-forward
                const bufferInfo = marketableBufferInfo.find(b => b.byproductItemNumber === entry.byproductItemNumber);
                // Carry-forward is buffer total BEFORE this session's contribution
                // After submission, buffer will be: carryForward + sessionWeight
                const carryForward = bufferInfo ? bufferInfo.totalBufferWeight : 0;
                const hasCarryForward = carryForward > 0;

                return (
                  <div key={`mk-${idx}`} className="px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm md:text-base font-medium text-yellow-900 dark:text-yellow-100 truncate mr-2">
                        {t('mobile.byProductMarketable') || 'Marketable'}
                        {mapping && <span className="ml-1 text-xs text-yellow-500 dark:text-yellow-400">({mapping.byproduct_name || entry.byproductItemNumber})</span>}
                      </span>
                      <span className="text-sm md:text-base font-bold text-yellow-700 dark:text-yellow-300 flex-shrink-0">{sessionWeight.toFixed(2)} lbs</span>
                    </div>
                    {/* Show carry-forward breakdown if there's prior buffer weight */}
                    {hasCarryForward && (
                      <div className="mt-1.5 pt-1.5 border-t border-yellow-200 dark:border-yellow-700/50">
                        <div className="flex justify-between text-xs text-yellow-600 dark:text-yellow-400">
                          <span>{t('mobile.thisSession') || 'This session'}</span>
                          <span>{sessionWeight.toFixed(2)} lbs</span>
                        </div>
                        <div className="flex justify-between text-xs text-yellow-600 dark:text-yellow-400">
                          <span>{t('mobile.carryForward') || 'Carry-forward'} <span className="opacity-70">({t('mobile.fromPriorSessions') || 'from prior sessions'})</span></span>
                          <span>{carryForward.toFixed(2)} lbs</span>
                        </div>
                        <div className="flex justify-between text-xs font-semibold text-yellow-700 dark:text-yellow-300 mt-0.5">
                          <span>{t('mobile.bufferTotal') || 'Buffer total'}</span>
                          <span>{(carryForward + sessionWeight).toFixed(2)} lbs</span>
                        </div>
                        {bufferInfo && bufferInfo.readyCaseCount > 0 && (
                          <div className="flex justify-between text-xs text-yellow-500 dark:text-yellow-500 mt-0.5">
                            <span>{t('mobile.readyCases') || 'Ready cases'}: {bufferInfo.readyCaseCount}</span>
                            <span>{t('mobile.reserve') || 'Reserve'}: {bufferInfo.reservedWeight.toFixed(2)} lbs</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="flex justify-between items-center px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <span className="text-sm md:text-base font-medium text-yellow-900 dark:text-yellow-100">
                  {t('mobile.byProductMarketable') || 'Marketable'}
                  <span className="ml-1 text-xs text-yellow-500 dark:text-yellow-400">({t('mobile.byProduct') || 'By-Product'})</span>
                </span>
                <span className="text-sm md:text-base font-bold text-yellow-700 dark:text-yellow-300">0.00 lbs</span>
              </div>
            )}
            {/* By-Product Production entries - Green */}
            {byproductProductionEntries.filter(e => e.byproductItemNumber && parseFloat(e.weight) > 0).length > 0 ? (
              byproductProductionEntries.filter(e => e.byproductItemNumber && parseFloat(e.weight) > 0).map((entry, idx) => {
                const w = parseFloat(entry.weight) || 0;
                const mapping = byproductMappings.find(m => m.byproduct_item_number === entry.byproductItemNumber);
                return (
                  <div key={`pr-${idx}`} className="flex justify-between items-center px-3 py-2 bg-lime-50 dark:bg-lime-900/20 border border-lime-200 dark:border-lime-800 rounded-lg">
                    <span className="text-sm md:text-base font-medium text-lime-900 dark:text-lime-100 truncate mr-2">
                      {t('mobile.byProductProduction') || 'Used for Production'}
                      {mapping && <span className="ml-1 text-xs text-lime-500 dark:text-lime-400">({mapping.byproduct_name || entry.byproductItemNumber})</span>}
                    </span>
                    <span className="text-sm md:text-base font-bold text-lime-700 dark:text-lime-300 flex-shrink-0">{w.toFixed(2)} lbs</span>
                  </div>
                );
              })
            ) : (
              <div className="flex justify-between items-center px-3 py-2 bg-lime-50 dark:bg-lime-900/20 border border-lime-200 dark:border-lime-800 rounded-lg">
                <span className="text-sm md:text-base font-medium text-lime-900 dark:text-lime-100">
                  {t('mobile.byProductProduction') || 'Used for Production'}
                  <span className="ml-1 text-xs text-lime-500 dark:text-lime-400">({t('mobile.byProduct') || 'By-Product'})</span>
                </span>
                <span className="text-sm md:text-base font-bold text-lime-700 dark:text-lime-300">0.00 lbs</span>
              </div>
            )}
            {/* Remainder Not Intact - Orange */}
            <div className="flex justify-between items-center px-3 py-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <span className="text-sm md:text-base font-medium text-orange-900 dark:text-orange-100">{t('mobile.remainderNotIntact') || 'Not Intact'}</span>
              <span className="text-sm md:text-base font-bold text-orange-700 dark:text-orange-300">{remainderNotIntact.toFixed(2)} lbs</span>
            </div>
            {/* Remainder Intact - Purple */}
            <div className="flex justify-between items-center px-3 py-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
              <span className="text-sm md:text-base font-medium text-purple-900 dark:text-purple-100">
                {t('mobile.intactBoxes') || 'Intact Boxes'}
                {scannedBoxes.length > 0 && <span className="ml-1 text-xs text-purple-500 dark:text-purple-400">({scannedBoxes.length} boxes)</span>}
              </span>
              <span className="text-sm md:text-base font-bold text-purple-700 dark:text-purple-300">{remainderIntact.toFixed(2)} lbs</span>
            </div>
          </div>
        </div>

        {/* Yield */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 md:p-4 flex justify-between items-center">
          <span className="text-sm md:text-base font-medium text-gray-700 dark:text-gray-300">{t('mobile.yieldRate') || 'Yield Rate'}</span>
          <span className={`text-lg md:text-xl font-bold ${yieldRate > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}>
            {formatYield(yieldRate)}
          </span>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={goBack} className="flex-1 py-3.5 md:py-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold text-sm md:text-base flex items-center justify-center gap-1.5">
          <ArrowLeft className="w-4 h-4" />
          {t('common.back') || 'Back'}
        </button>
        <button
          onClick={handleSubmitClick}
          className={`flex-1 py-3.5 md:py-4 rounded-lg font-semibold text-sm md:text-base flex items-center justify-center gap-1.5 ${
            waterLiquidNegative
              ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400'
              : 'bg-green-600 text-white'
          }`}
        >
          <CheckCircle className="w-4 h-4" />
          <span>{t('mobile.submitWaste') || 'Submit'}</span>
        </button>
      </div>
    </>
  );
};

export default WasteSummaryStep;
